import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { 
  getDevices,
  createDevice,
  deleteDevice,
  getOrganizations,
  getApiKey,
  addTimeToDevice,
  forceStopStream,
  DEFAULT_HOURLY_RATE,
  WS_URL,
  API_URL // Импортируем API_URL
} from "../api/api";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Container,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  AppBar,
  Toolbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Box,
  TextField,
  LinearProgress
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import KeyIcon from '@mui/icons-material/Key';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import TimerIcon from '@mui/icons-material/Timer';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from "@mui/icons-material/Stop";
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent'; // Иконка для API URL
import { Link } from "react-router-dom";
import { useCurrentTime } from "../hooks/useCurrentTime";

const DeviceList = () => {
  const [devices, setDevices] = useState([]);
  const [groupedDevices, setGroupedDevices] = useState({});
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openApiKeyDialog, setOpenApiKeyDialog] = useState(false);
  const [openAddTimeDialog, setOpenAddTimeDialog] = useState(false);
  const [openStopDialog, setOpenStopDialog] = useState(false);
  const [openApiUrlDialog, setOpenApiUrlDialog] = useState(false); // Новый диалог для API URL
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedDeviceForTime, setSelectedDeviceForTime] = useState(null);
  const [selectedDeviceForStop, setSelectedDeviceForStop] = useState(null);
  const [newOrgName, setNewOrgName] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [apiKey, setApiKey] = useState("");
  const [hoursToAdd, setHoursToAdd] = useState(1);
  const [alwaysExpanded, setAlwaysExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem('alwaysExpanded');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [expandedAccordions, setExpandedAccordions] = useState(() => {
    try {
      const saved = localStorage.getItem('expandedAccordions');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const devicesRef = useRef([]);
  const wsConnectedRef = useRef(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const lastMessageRef = useRef({}); // Для отслеживания дублирующихся сообщений
  
  useEffect(() => {
    devicesRef.current = devices;
  }, [devices]);

  useEffect(() => {
    const websocketUrl = WS_URL;
    isMountedRef.current = true;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 10;
    const reconnectDelay = 5000;

    const connectWebSocket = () => {
      if (!isMountedRef.current) return;
      
      // Закрываем предыдущее соединение, если оно есть
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
      
      try {
        console.log(`Подключение к WebSocket: ${websocketUrl}/ws`);
        const ws = new WebSocket(`${websocketUrl}/ws`);
        wsRef.current = ws;
        
        ws.onopen = () => {
          console.log('WebSocket соединение установлено');
          wsConnectedRef.current = true;
          reconnectAttempts = 0;
        };

        ws.onmessage = (event) => {
          try {
              const data = JSON.parse(event.data);
              console.log('📨 Сообщение WebSocket:', data);
              
              // Проверяем, не дублируется ли сообщение
              const messageKey = `${data.deviceId}_${data.updated_at}`;
              if (lastMessageRef.current[messageKey]) {
                console.log('Пропускаем дублирующееся сообщение');
                return;
              }
              
              lastMessageRef.current[messageKey] = true;
              
              // Очищаем старые записи (сохраняем только последние 100 сообщений)
              const keys = Object.keys(lastMessageRef.current);
              if (keys.length > 100) {
                delete lastMessageRef.current[keys[0]];
              }
              
              // Если есть deviceId - обновляем устройство
              if (data.deviceId) {
                  console.log(`🔄 Обновление устройства ${data.deviceId} с:`, data);
                  
                  setDevices(prev => prev.map(device => {
                      if (device.id === data.deviceId) {
                          const updatedDevice = { ...device };
                          
                          // Обновляем все поля, которые пришли
                          if (data.status !== undefined) {
                              updatedDevice.status = data.status;
                          }
                          if (data.streaming !== undefined) {
                              updatedDevice.streaming = data.streaming;
                          }
                          if (data.stream_until !== undefined) {
                              updatedDevice.stream_until = data.stream_until;
                          }
                          if (data.updated_at !== undefined) {
                              updatedDevice.updated_at = data.updated_at;
                          }
                          if (data.last_activity !== undefined) {
                              updatedDevice.last_activity = data.last_activity;
                          }
                          
                          console.log('Обновленное устройство:', updatedDevice);
                          return updatedDevice;
                      }
                      return device;
                  }));
              } else {
                  console.log('Получено сообщение WebSocket без deviceId:', data);
              }
          } catch (error) {
              console.error('❌ Ошибка сообщения WebSocket:', error, 'Данные:', event.data);
          }
        };

        ws.onclose = (event) => {
          console.log(`WebSocket соединение закрыто: ${event.code} ${event.reason}`);
          wsConnectedRef.current = false;
          
          if (isMountedRef.current && reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            const delay = reconnectDelay * Math.pow(1.5, reconnectAttempts - 1);
            console.log(`Переподключение через ${delay/1000} секунд (попытка ${reconnectAttempts})`);
            
            reconnectTimeoutRef.current = setTimeout(() => {
              connectWebSocket();
            }, delay);
          } else {
            console.log('Достигнуто максимальное количество попыток переподключения');
          }
        };

        ws.onerror = (error) => {
          console.error('Ошибка WebSocket:', error);
        };
      } catch (error) {
        console.error('Не удалось создать WebSocket:', error);
        if (isMountedRef.current) {
          reconnectTimeoutRef.current = setTimeout(connectWebSocket, reconnectDelay);
        }
      }
    };

    // Задержка перед первым подключением
    const initialTimeout = setTimeout(connectWebSocket, 1000);

    return () => {
      isMountedRef.current = false;
      clearTimeout(initialTimeout);
      clearTimeout(reconnectTimeoutRef.current);
      
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        console.log('WebSocket соединение очищено');
      }
    };
  }, []);

  useEffect(() => {
    const groups = devices.reduce((acc, device) => {
      const key = device.org_name || 'unknown';
      if (!acc[key]) acc[key] = [];
      acc[key].push(device);
      return acc;
    }, {});
    
    setGroupedDevices(groups);
  }, [devices]);

  // Сохранение состояния аккордеонов
  useEffect(() => {
    localStorage.setItem('expandedAccordions', JSON.stringify(expandedAccordions));
  }, [expandedAccordions]);

  // Сохранение состояния alwaysExpanded
  useEffect(() => {
    localStorage.setItem('alwaysExpanded', JSON.stringify(alwaysExpanded));
  }, [alwaysExpanded]);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await getDevices();
      const devicesData = response.data || [];
      setDevices(devicesData);
      
      const groups = devicesData.reduce((acc, device) => {
        const key = device.org_name || 'unknown';
        if (!acc[key]) acc[key] = [];
        acc[key].push(device);
        return acc;
      }, {});

      setGroupedDevices(groups);
    } catch (error) {
      console.error("Не удалось загрузить устройства:", error);
      setSnackbar({ 
        open: true, 
        message: "Не удалось загрузить устройства", 
        severity: "error" 
      });
    }
  }, []);

  useEffect(() => {
    fetchDevices();
    fetchOrganizations();
  }, [fetchDevices]);

  // Обработчик изменения аккордеона
  const handleAccordionChange = (orgName) => (event, isExpanded) => {
    if (event.target.type === 'checkbox') return;
    
    setExpandedAccordions(prev => ({
      ...prev,
      [orgName]: alwaysExpanded[orgName] ? true : isExpanded
    }));
  };

  // Обработчик чекбокса
  const handleAlwaysExpandedChange = (orgName) => {
    const newValue = !alwaysExpanded[orgName];
    
    setAlwaysExpanded(prev => ({
      ...prev,
      [orgName]: newValue
    }));

    setExpandedAccordions(prev => ({
      ...prev,
      [orgName]: newValue
    }));
  };

  const fetchOrganizations = async () => {
    try {
      const response = await getOrganizations();
      setOrganizations(response.data || []);
    } catch (error) {
      console.error("Не удалось загрузить организации:", error);
      setSnackbar({ open: true, message: "Не удалось загрузить организации", severity: "error" });
      setOrganizations([]);
    }
  };

  const handleAddDevice = async () => {
    if (!newOrgName.trim()) {
      setSnackbar({ open: true, message: "Необходимо выбрать организацию", severity: "error" });
      return;
    }

    try {
      const selectedOrg = organizations.find(org => org.name === newOrgName);
      
      if (!selectedOrg) {
        throw new Error("Выбрана неверная организация");
      }

      await createDevice({ 
        org_id: selectedOrg.id
      });
      setNewOrgName("");
      setOpenAddDialog(false);
      await fetchDevices();
      setSnackbar({ open: true, message: "Устройство успешно добавлено", severity: "success" });
    } catch (error) {
      console.error("Не удалось добавить устройство:", error);
      setSnackbar({ open: true, message: "Не удалось добавить устройство", severity: "error" });
    }
  };

  const handleDeleteDevice = async () => {
    try {
      await deleteDevice(selectedDevice);
      setOpenDeleteDialog(false);
      await fetchDevices();
      setSnackbar({ open: true, message: "Устройство успешно удалено", severity: "success" });
    } catch (error) {
      console.error("Не удалось удалить устройство:", error);
      setSnackbar({ open: true, message: "Не удалось удалить устройство", severity: "error" });
    }
  };

  // Обработчик получения API-ключа для устройства
  const handleGetApiKey = async (deviceId) => {
    try {
      const response = await getApiKey(deviceId);
      if (response.data && response.data.api_key) {
        setApiKey(response.data.api_key);
        setOpenApiKeyDialog(true);
      } else {
        setSnackbar({ 
          open: true, 
          message: "API ключ не найден для этого устройства", 
          severity: "warning" 
        });
      }
    } catch (error) {
      console.error("Не удалось получить API ключ:", error);
      setSnackbar({ 
        open: true, 
        message: "Не удалось получить API ключ", 
        severity: "error" 
      });
    }
  };

  // Обработчик добавления времени
  const handleAddTime = async () => {
    try {
      const device = devices.find(d => d.id === selectedDeviceForTime);
      const deviceOrg = organizations.find(org => org.id === device?.org_id);
      const hourlyRate = deviceOrg?.hourly_rate > 0 ? deviceOrg.hourly_rate : DEFAULT_HOURLY_RATE;
      
      await addTimeToDevice(selectedDeviceForTime, hoursToAdd);
      setOpenAddTimeDialog(false);
      setHoursToAdd(1);
      
      await fetchDevices();
      
      const totalCost = (hoursToAdd * hourlyRate).toFixed(2);
      setSnackbar({ 
        open: true, 
        message: `Успешно добавлено ${hoursToAdd} час(а/ов). Стоимость: ${totalCost} ₽`, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Не удалось добавить время:', error);
      const errorMessage = error.response?.data?.message || 'Ошибка добавления времени';
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    }
  };

  // Обработчик остановки
  const handleStop = async () => {
    try {
      await forceStopStream(selectedDeviceForStop);
      setOpenStopDialog(false);
      
      // Перезагружаем данные
      await fetchDevices();
      
      setSnackbar({ 
        open: true, 
        message: 'Успешно остановлено', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Не удалось остановить:', error);
      setSnackbar({ 
        open: true, 
        message: 'Не удалось остановить', 
        severity: 'error' 
      });
    }
  };

  // Функция для копирования API URL
  const copyApiUrlToClipboard = () => {
    navigator.clipboard.writeText(API_URL);
    setSnackbar({
      open: true,
      message: "URL API скопирован в буфер обмена",
      severity: "success"
    });
  };

  // Функция для форматирования даты и времени
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Неизвестно';
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Компонент для отображения информации о времени устройства
  const TimeStatusDisplay = ({ device }) => {
    const currentTime = useCurrentTime(1000);

    // Вычисления
    const remainingSeconds = useMemo(() => {
      if (!device?.stream_until) return 0;
      
      const streamUntil = new Date(device.stream_until);
      if (streamUntil <= currentTime) return 0;
      
      const diffMs = streamUntil - currentTime;
      return Math.floor(diffMs / 1000);
    }, [device, currentTime]);

    const formattedTime = useMemo(() => {
      if (remainingSeconds <= 0) return "00:00";
      
      const hours = Math.floor(remainingSeconds / 3600);
      const minutes = Math.floor((remainingSeconds % 3600) / 60);
      const seconds = remainingSeconds % 60;
      
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      }
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }, [remainingSeconds]);

    const progressValue = useMemo(() => {
      const minutes = Math.floor(remainingSeconds / 60);
      if (remainingSeconds <= 0) return 0;
      if (minutes >= 60) return 100;
      return (minutes / 60) * 100;
    }, [remainingSeconds]);

    const progressColor = useMemo(() => {
      const minutes = Math.floor(remainingSeconds / 60);
      if (remainingSeconds <= 0) return 'error';
      if (minutes >= 60) return 'success';
      if (minutes > 5) return 'warning';
      return 'error';
    }, [remainingSeconds]);

    const hasActiveTime = device?.streaming && remainingSeconds > 0;

    if (!hasActiveTime) {
      return (
        <Typography 
          variant="body2" 
          color="error"
          component="div"
          fontWeight="medium"
          sx={{ mt: 1 }}
        >
          ⏱️ Время вышло
        </Typography>
      );
    }

    return (
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TimerIcon fontSize="small" color="primary" sx={{ fontSize: 16 }} />
          <Typography variant="body2" fontWeight="medium" component="span">
            {formattedTime}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progressValue} 
          color={progressColor}
          sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
        />
      </Box>
    );
  };

  // Кнопка для получения API-ключа
  const getApiKeyButton = (deviceId) => (
    <Tooltip title="Получить ключ API">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          handleGetApiKey(deviceId);
        }}
        color="primary"
        size="small"
        aria-label={`Получить API ключ для устройства ${deviceId}`}
      >
        <KeyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  // Кнопка добавления времени
  const getAddTimeButton = (device) => (
    <Tooltip title="Добавить время">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          setSelectedDeviceForTime(device.id);
          setOpenAddTimeDialog(true);
        }}
        color="primary"
        size="small"
        aria-label={`Добавить время устройству ${device.id}`}
        disabled={device?.status !== "Active"}
      >
        <AddIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  // Кнопка остановки
  const getStopButton = (device) => (
    <Tooltip title="Остановить">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          setSelectedDeviceForStop(device.id);
          setOpenStopDialog(true);
        }}
        color="error"
        size="small"
        aria-label={`Остановить устройство ${device.id}`}
        disabled={!device?.streaming}
      >
        <StopIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  // Кнопка копирования ID устройства
  const getCopyIdButton = (device) => (
    <Tooltip title="Копировать ID">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          copyDeviceIdToClipboard(device.id);
        }}
        color="default"
        size="small"
        aria-label={`Копировать ID устройства ${device.id}`}
      >
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  // Копирование API-ключа в буфер обмена
  const copyApiKeyToClipboard = () => {
    navigator.clipboard.writeText(apiKey);
    setSnackbar({
      open: true,
      message: "API ключ скопирован в буфер обмена",
      severity: "success"
    });
  };

  // Копирование ID устройства в буфер обмена
  const copyDeviceIdToClipboard = (deviceId) => {
    navigator.clipboard.writeText(deviceId);
    setSnackbar({
      open: true,
      message: "ID устройства скопирован в буфер обмена",
      severity: "success"
    });
  };

  // Функция для получения правильного окончания слова "устройство"
  const getDevicesWord = (count) => {
    if (count % 10 === 1 && count % 100 !== 11) return 'устройство';
    if (count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 10 || count % 100 >= 20)) return 'устройства';
    return 'устройств';
  };

  // Функция для получения правильного окончания слова "час"
  const getHoursWord = (hours) => {
    if (hours % 10 === 1 && hours % 100 !== 11) return 'час';
    if (hours % 10 >= 2 && hours % 10 <= 4 && (hours % 100 < 10 || hours % 100 >= 20)) return 'часа';
    return 'часов';
  };

  return (
    <Container>
      {/* Список устройств */}
      <Typography variant="h4" align="center" gutterBottom sx={{ mt: 3 }}>
        Устройства по организациям
      </Typography>

      {/* Панель с кнопками */}
      <AppBar position="static" elevation={0} sx={{ backgroundColor: "transparent", mb: 3 }}>
        <Toolbar disableGutters>
          {/* Кнопка добавления устройства */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
            sx={{ ml: "auto" }}
            aria-label="Добавить устройство"
          >
            Добавить устройство
          </Button>
          
          {/* Кнопка копирования API URL с отличной иконкой */}
          <Tooltip title="Копировать URL API">
            <IconButton
              onClick={copyApiUrlToClipboard}
              color="primary"
              sx={{ ml: 2 }}
              aria-label="Копировать URL API"
            >
              <SettingsInputComponentIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Диалог API ключа */}
      <Dialog open={openApiKeyDialog} onClose={() => setOpenApiKeyDialog(false)}>
        <DialogTitle>Ключ API устройства</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Это ваш ключ API устройства. Сохраните его в безопасном месте, так как он больше не будет показан.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              value={apiKey}
              fullWidth
              variant="outlined"
              InputProps={{
                readOnly: true,
              }}
              id="api-key-field"
              label="Ключ API"
            />
            <Tooltip title="Скопировать в буфер обмена">
              <IconButton onClick={copyApiKeyToClipboard} sx={{ ml: 1 }} aria-label="Копировать API ключ">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApiKeyDialog(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог добавления времени */}
      <Dialog open={openAddTimeDialog} onClose={() => setOpenAddTimeDialog(false)}>
        <DialogTitle>Добавить время</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Добавьте оплаченное время для этого устройства. Если устройство не активно, оно автоматически запустится.
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            label="Часы для добавления"
            type="number"
            fullWidth
            variant="outlined"
            value={hoursToAdd}
            onChange={(e) => setHoursToAdd(parseFloat(e.target.value))}
            inputProps={{ 
              min: 0.1, 
              max: 24, 
              step: 0.1
            }}
            helperText="Введите часы (0.1 - 24)"
          />
          
          <Typography variant="body2" sx={{ mt: 2, color: 'primary.main' }}>
            Примерная стоимость: {(hoursToAdd * DEFAULT_HOURLY_RATE).toFixed(2)} ₽ (по {DEFAULT_HOURLY_RATE} ₽/час)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddTimeDialog(false)}>Отмена</Button>
          <Button 
            onClick={handleAddTime} 
            variant="contained" 
            disabled={hoursToAdd <= 0 || hoursToAdd > 24}
          >
            Добавить {hoursToAdd} {getHoursWord(hoursToAdd)}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог остановки */}
      <Dialog open={openStopDialog} onClose={() => setOpenStopDialog(false)}>
        <DialogTitle>Остановить</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите остановить? Неиспользованное время будет сохранено для будущего использования.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStopDialog(false)}>Отмена</Button>
          <Button 
            onClick={handleStop} 
            color="error" 
            variant="contained"
          >
            Остановить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог добавления устройства */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Добавить устройство</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="organization-select-label">Организация</InputLabel>
            <Select
              value={newOrgName}
              onChange={(e) => setNewOrgName(e.target.value)}
              label="Организация"
              labelId="organization-select-label"
              inputProps={{ id: "organization-select" }}
            >
              {(organizations || []).map((org) => (
                <MenuItem key={org.id} value={org.name}>
                  {org.name} ({org.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Отмена</Button>
          <Button onClick={handleAddDevice} variant="contained" color="primary">
            Добавить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Подтвердите удаление</DialogTitle>
        <DialogContent>
          <Typography>Вы уверены, что хотите удалить это устройство?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Отмена</Button>
          <Button onClick={handleDeleteDevice} color="error" variant="contained">
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Уведомления */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {(groupedDevices && Object.keys(groupedDevices || {}).length === 0 ? (
        <Typography variant="body1" sx={{ p: 2, color: 'text.secondary' }}>
          Устройства не найдены.
        </Typography>
      ) : (
        Object.keys(groupedDevices || {}).sort().map((org_name) => {
          const orgDevices = groupedDevices?.[org_name] || [];
          const deviceCount = orgDevices.length;
          return (
            <Accordion 
              key={org_name} 
              sx={{ mb: 2 }}
              expanded={alwaysExpanded[org_name] || expandedAccordions[org_name] || false}
              onChange={handleAccordionChange(org_name)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!alwaysExpanded[org_name]}
                      onChange={() => handleAlwaysExpandedChange(org_name)}
                      onClick={(e) => e.stopPropagation()}
                      id={`always-expanded-${org_name}`}
                    />
                  }
                  label={
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      Организация: {org_name}
                    </Typography>
                  }
                />
                <Chip
                  label={`${deviceCount} ${getDevicesWord(deviceCount)}`}
                  color="primary"
                  sx={{ mr: 2 }}
                />
              </AccordionSummary>
              <AccordionDetails>
                <List>
                {orgDevices.map((device) => (
                  <ListItem 
                    key={`${device.id}-${device.updated_at || device.created_at}`}
                    disablePadding
                    secondaryAction={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {getAddTimeButton(device)}
                        {getStopButton(device)}
                        {getCopyIdButton(device)}
                        {getApiKeyButton(device.id)}
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (device?.id) {
                              setSelectedDevice(device.id);
                              setOpenDeleteDialog(true);
                            }
                          }}
                          size="small"
                        >
                          <DeleteIcon color="error" fontSize="small" />
                        </IconButton>
                      </div>
                    }
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        cursor: 'pointer'
                      },
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <ListItemButton 
                      component={Link}
                      to={`/devices/${device?.id}`}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'transparent'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box component="span">
                            <Typography 
                              component="span"
                              variant="body1" 
                              sx={{ 
                                color: device?.status === 'Active' ? '#2e7d32' : '#d32f2f',
                                fontWeight: 'bold',
                                display: 'inline-block',
                                mr: 1
                              }}
                            >
                              {device?.status === 'Active' ? 'Активно' : 
                               device?.status === 'Inactive' ? 'Неактивно' : 
                               device?.status || 'Неизвестный статус'}
                            </Typography>
                            <Typography 
                              component="div"
                              variant="body2"
                              color="text.primary"
                            >
                              ID устройства: {device?.id || 'Неизвестный ID'}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box component="div">
                            <Typography 
                              variant="body2" 
                              component="div"
                              color="text.secondary"
                            >
                              Создано: {formatDateTime(device?.created_at)}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              component="div"
                              color="text.secondary"
                            >
                              Последняя активность: {formatDateTime(device?.last_activity || device?.updated_at)}
                            </Typography>
                            <TimeStatusDisplay device={device} />
                          </Box>
                        }
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                </List>
              </AccordionDetails>
            </Accordion>
          );
        })
      ))}
      
    </Container>
  );
};

export default DeviceList;